var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { CheckableTagSelectItem, FlexBox, Select, TagSelectItem, TagSelectSuggestion, } from '@dixa/lib-shared-habla';
import { sizes } from '@dixa/lib-shared-tokens';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { tagsApi } from 'apis/tags';
import { analytics } from 'app/utils/segment';
import { stringToHexColor } from 'components/shared/util/color';
import { isResponseWithData } from 'utils/apis/axios';
import { usePaginatedEndpoint } from 'utils/apis/pagination/usePaginatedEndpoint';
import styles from './tagSelect.module.scss';
/** Represents the component for selecting one or more tags. */
export const TagSelect = ({ className, selectedTags = [], infiniteScroll, includeDeactivated, allowSelectDeactivated, allowMultiple, allowTagCreation, placeholder, onChange, }) => {
    var _a, _b;
    const [currentSelectedTags, setCurrentSelectedTags] = useState(selectedTags);
    const [search, setSearch] = useState({});
    const { arg, asyncStatus, hasNextPage, hasPreviousPage, items: tags, changeArg, nextPage, previousPage, } = usePaginatedEndpoint({
        endpoint: tagsApi.endpoints.readTags,
        itemsSelector: ({ data: { tags } }) => tags,
        initialArg: {
            state: (includeDeactivated !== true && 'active') || undefined,
            sort: { key: 'name', direction: 'asc' },
        },
        infinite: infiniteScroll,
    });
    const [createTag] = tagsApi.useCreateTagMutation();
    const disabledTags = useMemo(() => includeDeactivated && !allowSelectDeactivated
        ? tags.filter((tag) => tag.is_deactivated).map((tag) => tag.id)
        : [], [allowSelectDeactivated, includeDeactivated, tags]);
    const doesTagNameExist = useMemo(() => tags.some((tag) => { var _a; return tag.name.toLowerCase() === ((_a = search.term) === null || _a === void 0 ? void 0 : _a.toLowerCase()); }), [search.term, tags]);
    const shouldShowSuggestion = allowTagCreation &&
        !doesTagNameExist &&
        ((_b = (_a = search.term) === null || _a === void 0 ? void 0 : _a.trim().length) !== null && _b !== void 0 ? _b : 0) > 1;
    /** Handles click on new tag suggestion. */
    const handleSuggest = useCallback((name) => __awaiter(void 0, void 0, void 0, function* () {
        analytics.tagSelectCreateNewTagClicked({
            tagName: name,
            component: 'TagSelect',
        });
        const response = yield createTag({ name, color: stringToHexColor(name) });
        if (isResponseWithData(response)) {
            setCurrentSelectedTags([...currentSelectedTags, response.data.data]);
        }
    }), [createTag, currentSelectedTags]);
    /** Handles searching the tags. */
    const handleSearch = useCallback((value) => {
        setSearch({ term: value.trim() || undefined });
    }, []);
    // Changes the arg when search term changes.
    useEffect(() => {
        var _a;
        if (search.term !== (arg === null || arg === void 0 ? void 0 : arg.searchTerm)) {
            changeArg({
                searchTerm: search.term,
                offset: undefined,
                limit: undefined,
            }, { debounceTime: (_a = search === null || search === void 0 ? void 0 : search.debounceTime) !== null && _a !== void 0 ? _a : 300 });
        }
        // Don't rely on arg as dependency because we change the arg inside.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [changeArg, search]);
    /** Handles the change of selection. */
    const handleChange = useCallback((tag, checked) => {
        setCurrentSelectedTags(checked
            ? [...currentSelectedTags, tag]
            : currentSelectedTags.filter(({ id }) => id !== tag.id));
    }, [currentSelectedTags]);
    // Dispatches the on change even when selected tags are updated.
    useEffect(() => {
        onChange === null || onChange === void 0 ? void 0 : onChange(currentSelectedTags);
    }, [onChange, currentSelectedTags]);
    /** Handles toggling the dropdown of the select component. */
    const handleToggle = useCallback((isOpen) => {
        if (isOpen) {
            setSearch({ debounceTime: 0 });
        }
    }, []);
    /** Handles navigating to the next page. */
    const handleNextPage = useCallback(() => {
        return Promise.resolve(nextPage());
    }, [nextPage]);
    /** Handles navigating to the previous page. */
    const handlePreviousPage = useCallback(() => {
        return Promise.resolve(previousPage());
    }, [previousPage]);
    /** Takes search string and returns suggestion item content */
    const renderSuggestion = useCallback((search) => (_jsxs(FlexBox, Object.assign({ gap: sizes.spacer[300], alignItems: "center" }, { children: [_jsx("div", { className: styles.tagColor, style: { backgroundColor: stringToHexColor(search) } }), _jsxs("div", { children: ["Create \"", search, "\" tag"] })] }))), []);
    return (_jsx(Select, { className: className, multiple: allowMultiple, searchable: true, emptyMessage: search.term ? 'No tags found' : 'No tags available', 
        // have to re-cast because readonly type
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        items: tags, 
        // have to re-cast because readonly type
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        selectedItems: currentSelectedTags, disabledItems: disabledTags, itemComponent: allowMultiple ? CheckableTagSelectItem : TagSelectItem, loading: asyncStatus === 'pending', onSearch: handleSearch, onToggle: handleToggle, onChange: handleChange, pagination: {
            infinite: infiniteScroll,
            nextEnable: hasNextPage,
            previousEnable: hasPreviousPage,
            onNext: handleNextPage,
            onPrevious: handlePreviousPage,
        }, placeholder: placeholder !== null && placeholder !== void 0 ? placeholder : (allowMultiple ? 'Select one or more tags' : 'Select a tag'), onSuggest: handleSuggest, suggestionComponent: shouldShowSuggestion ? TagSelectSuggestion : undefined, renderSuggestion: renderSuggestion }));
};
